import { ApolloError, useMutation } from '@apollo/client';
import { showToast } from 'components/common/CustomToast';
import AccountContext from 'context/AccountContext';
import { UPDATE_PARKING_SESSION } from 'graphql/mutations/updateParkingSession';
import { READ_PARKING_SESSION } from 'graphql/queries/readParkingSession';
import { READ_PARKING_SESSIONS } from 'graphql/queries/readParkingSessions';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParkingStore } from 'stores/parkingStore';
import {
  Mutation,
  MutationUpdateParkingSessionArgs,
  ParkingSession,
  PaymentType,
  SelectedSegment,
  UpdateParkingSessionActionType,
} from 'types/generatedSchemaTypes';
import { checkNotificationsPermissions } from 'utils/deviceUtils';
import Logger, { FlowStatus, UserFlowName } from 'utils/Logger';
import { useUiStore } from 'stores/uiStore';

type UpdateParkingProps = {
  sessionId: string;
  isModal?: boolean;
  actionType: UpdateParkingSessionActionType;
  newEndDate?: Date;
  cardId?: string;
  licensePlate?: string;
  callback?: (
    data: string,
    action: UpdateParkingSessionActionType,
    updatedParking: ParkingSession
  ) => any;
  errorCallback?: (error: ApolloError) => any;
  skipRefetch?: boolean;
  anprManualStop?: boolean;
  endDate?: Date;
  customerSegment?: SelectedSegment;
  paymentMethodType?: PaymentType;
};
const useUpdateParkingMutation = ({
  sessionId,
  isModal = false,
  actionType,
  newEndDate,
  cardId,
  licensePlate,
  callback,
  errorCallback,
  skipRefetch = false,
  anprManualStop = false,
  endDate,
  customerSegment,
  paymentMethodType,
}: UpdateParkingProps) => {
  const [updateComplete, setUpdateComplete] = useState<boolean>(false);
  const { refetchCustomer } = useContext(AccountContext);
  const parkingSession = useParkingStore(
    (state) => state.ongoingParkingSession
  );
  // Flow id of the create-session instance that started THIS session, so
  // stop/extend logs land on the same trace. Sessions not created in this app
  // run (e.g. already ongoing at launch) have no entry and get their own id,
  // generated once on mount so it stays stable across renders.
  const createdFlowId = useParkingStore(
    (state) => state.parkingSessionFlowIds[sessionId]
  );
  const [fallbackFlowId] = useState(Logger.generateFlowId);
  const parkingFlowId = createdFlowId ?? fallbackFlowId;

  const memoizeEndTime = useMemo(() => {
    return parkingSession?.endTime;
  }, [parkingSession?.endTime]);
  const prevValueRef = useRef(memoizeEndTime);
  const hasUpdateCompletedAndValueChanged =
    prevValueRef.current !== memoizeEndTime;
  const [updateParking, { loading: updateParkingLoading }] = useMutation<
    Mutation,
    MutationUpdateParkingSessionArgs
  >(UPDATE_PARKING_SESSION, {
    awaitRefetchQueries:
      actionType === 'EXTEND' || actionType === 'RETRY_PAYMENT',
    refetchQueries: skipRefetch
      ? []
      : [
          {
            query: READ_PARKING_SESSIONS,
            variables: {
              input: {
                parkingState: ['Started'],
                sessionType: ['ANPR', 'START_STOP'],
              },
            },
          },
          {
            query: READ_PARKING_SESSION,
            variables: { sessionId },
          },
        ],
    variables: {
      input: {
        sessionId,
        actionType,
        newEndDate,
        cardId,
        licensePlate,
        anprManualStop,
        endDate,
        customerSegment,
        paymentMethodType,
      },
    },
    onCompleted: (data) => {
      // no need to show toast when stopping parking
      if (callback) {
        callback(
          sessionId,
          actionType,
          data.updateParkingSession as ParkingSession
        );
      }
      if (actionType === 'EXTEND') {
        setUpdateComplete(true);
        Logger.logUserFlow(
          UserFlowName.CreateParkingSession,
          parkingFlowId,
          FlowStatus.Updated,
          { parking_session_id: sessionId, action_type: actionType }
        );
      }

      if (actionType === 'STOP') {
        Logger.logUserFlow(
          UserFlowName.EndParkingSession,
          parkingFlowId,
          FlowStatus.Completed,
          { parking_session_id: sessionId, action_type: actionType }
        );
        useParkingStore.getState().clearParkingSessionFlowId(sessionId);
      }
      if (isModal && actionType !== 'EXTEND') {
        useUiStore.getState().setSheetModal('');
        useParkingStore.getState().setSelectedSessionIdFromMultiple(null);
      }
      useParkingStore.getState().setSelectedOngoingSession(undefined);
    },
    onError: (error) => {
      const errorCode = error.graphQLErrors[0]?.extensions?.code as
        | string
        | undefined;
      if (actionType === 'STOP') {
        showToast('error.parkingStopped', 'error');
        Logger.logUserFlow(
          UserFlowName.EndParkingSession,
          parkingFlowId,
          FlowStatus.Failed,
          {
            error_message: error.message,
            error_code: errorCode,
            action_type: actionType,
            parking_session_id: sessionId,
          }
        );
      } else if (actionType === 'EXTEND') {
        Logger.logUserFlow(
          UserFlowName.CreateParkingSession,
          parkingFlowId,
          FlowStatus.Failed,
          {
            error_message: error.message,
            error_code: errorCode,
            action_type: actionType,
            parking_session_id: sessionId,
          }
        );
        if (errorCode === 'PARKING_EXTENDED_FORBIDDEN') {
          showToast('error.timePicker.invalidTimeInFuture', 'info');
        } else {
          showToast('error.parkingExtended', 'error');
        }
      } else if (actionType === 'RETRY_PAYMENT') {
        if (
          error.graphQLErrors.some((e) => e.extensions?.code === 'CARD_BLOCKED')
        ) {
          showToast('error.cardBlocked', 'info');
          refetchCustomer();
        } else {
          showToast('error.parkingRetry', 'error');
        }
      } else if (actionType === 'UPDATE') {
        if (errorCallback) {
          errorCallback(error);
        }
      }
    },
  });

  useEffect(() => {
    const checkUpdatedValue = async () => {
      const notificationPermission = checkNotificationsPermissions('granted');
      if (
        isModal &&
        actionType === 'EXTEND' &&
        hasUpdateCompletedAndValueChanged
      ) {
        if (!notificationPermission) {
          showToast('success.parkingExtended', 'success');
        }

        useUiStore.getState().setSheetModal('');
        setUpdateComplete(false);
        useParkingStore.getState().setSelectedSessionIdFromMultiple(null);
      }
    };
    checkUpdatedValue();
  }, [isModal, actionType, hasUpdateCompletedAndValueChanged]);
  return {
    updateParking,
    updateParkingLoading,
    callback,
    hasUpdateCompletedAndValueChanged,
    updateComplete,
  };
};

export default useUpdateParkingMutation;
